/**
 * Email (or re-email) the combined invite, next to the button that copies it.
 *
 * Saving a show never re-sends its invite, so this is how a programmer re-invites:
 * the artist lost the email, or someone else on the bill should fill in the show.
 * It lives beside `InviteLinkButton` on cards, list rows and the dialog header because
 * that is where programmers look when they think "get this artist their link".
 *
 * Emailing a real artist is outward-facing, and cards and rows are easy to misclick, so
 * it always confirms first and names the recipient plus when they were last emailed.
 * Nothing is fetched until the button is clicked, so list rendering stays query-free.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import {
    createCompletionReminder,
    deleteCombinedRemindersForHappening,
    getCombinedRemindersForHappening,
    getPerson,
    updateHappening,
    updatePerson,
    type CompletionReminder,
    type Happening,
} from '@littlebooker/dal';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface EmailInviteButtonProps {
    happening: Happening;
    /** Who fills in the show when nobody has been invited yet (usually `pickShowFillerArtist`). */
    personId: string | null;
    /** 'button' is a regular outline button, for use inside a form section. */
    appearance?: 'card' | 'inline' | 'button';
    /** Overrides the "Email invite" label. */
    label?: string;
    /** Called with the queued reminder, and the show as it is now (its link may have just been minted). */
    onQueued?: (reminder: CompletionReminder, happening: Happening) => void;
    className?: string;
}

type Recipient = { id: string; name: string; email: string | null };

export function EmailInviteButton({ happening, personId, appearance = 'inline', label = 'Email invite', onQueued, className }: EmailInviteButtonProps) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recipient, setRecipient] = useState<Recipient | null>(null);
    const [previous, setPrevious] = useState<CompletionReminder | null>(null);

    const onBill = (id: string) => (happening.metadata?.artists ?? []).some((a) => a.person_id === id);

    const openConfirm = async () => {
        setOpen(true);
        setLoading(true);
        setError(null);
        try {
            const existing = (await getCombinedRemindersForHappening(happening.id))[0] ?? null;
            setPrevious(existing);
            // Re-invite whoever got the invite last, as long as they are still on the bill.
            const targetId = existing && onBill(existing.entity_id) ? existing.entity_id : personId;
            const row = targetId ? await getPerson(targetId) : null;
            setRecipient(row ? { id: row.id, name: row.name || 'the artist', email: row.email ?? null } : null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load the invite');
        } finally {
            setLoading(false);
        }
    };

    const send = async () => {
        if (!recipient?.email) return;
        setSending(true);
        setError(null);
        try {
            // Mint only what is missing. Regenerating an existing token would break the
            // links already sitting in the artist's inbox.
            const row = await getPerson(recipient.id);
            if (row && !row.profile_completion_token) {
                await updatePerson(row.id, { generateInviteLink: true });
                queryClient.invalidateQueries({ queryKey: ['people'] });
            }
            let current = happening;
            if (!current.metadata?.stub_token) {
                current = await updateHappening(current.id, { generateStubLink: true });
                queryClient.invalidateQueries({ queryKey: ['happenings'] });
            }

            // One combined invite per show: replace rather than add, so a change of who
            // fills in the show does not leave the previous artist's invite queued too.
            await deleteCombinedRemindersForHappening(current.id);
            const reminder = await createCompletionReminder({
                reminder_type: 'combined_artist_show',
                entity_id: recipient.id,
                happening_id: current.id,
                recipient_email: recipient.email,
                recipient_name: recipient.name,
                offset_hours: 0,
            });
            onQueued?.(reminder, current);
            setOpen(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not queue the invite');
        } finally {
            setSending(false);
        }
    };

    const pending = !!previous && !previous.sent_at && !previous.error_message;

    return (
        // Cards and rows open the edit dialog on click; this must not.
        <span className={cn('inline-flex', appearance === 'card' && 'shrink-0', className)} onClick={(e) => e.stopPropagation()}>
            {appearance === 'button' ? (
                <Button type="button" variant="outline" size="sm" onClick={openConfirm} disabled={!personId}>
                    <Mail className="h-4 w-4 mr-1" />
                    {label}
                </Button>
            ) : (
                <button
                    type="button"
                    onClick={openConfirm}
                    disabled={!personId}
                    title={personId ? 'Email the combined invite to the artist' : 'Add an artist to email an invite'}
                    className={cn(
                        appearance === 'card'
                            ? 'flex items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/40 px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/5'
                            : 'inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80',
                        !personId && 'opacity-50 cursor-not-allowed',
                    )}
                >
                    <Mail className="h-3.5 w-3.5" />
                    <span>{label}</span>
                </button>
            )}

            <Dialog open={open} onOpenChange={(next) => !sending && setOpen(next)}>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{previous ? 'Resend the combined invite?' : 'Email the combined invite?'}</DialogTitle>
                        <DialogDescription>
                            One email with one link where the artist completes their profile and fills in the show details.
                        </DialogDescription>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking what was already sent…
                        </div>
                    ) : (
                        <div className="space-y-2 text-sm">
                            {recipient ? (
                                recipient.email ? (
                                    <p>
                                        To <strong>{recipient.name}</strong> ({recipient.email}), on the next scheduler run (within 15 minutes).
                                    </p>
                                ) : (
                                    <p className="text-destructive">{recipient.name} has no email address.</p>
                                )
                            ) : (
                                <p className="text-destructive">No artist to invite on this show.</p>
                            )}
                            {previous?.sent_at && (
                                <p className="text-muted-foreground">
                                    Last sent to {previous.recipient_email} on {new Date(previous.sent_at).toLocaleString()}.
                                </p>
                            )}
                            {previous?.error_message && (
                                <p className="text-destructive">Last attempt was not sent: {previous.error_message}</p>
                            )}
                            {pending && (
                                <p className="text-amber-700">
                                    An invite to {previous.recipient_email} is already queued and has not gone out yet.
                                </p>
                            )}
                            {error && <p className="text-destructive">{error}</p>}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={sending}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={send} disabled={loading || sending || pending || !recipient?.email}>
                            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                            {previous ? 'Resend invite' : 'Send invite'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </span>
    );
}
